<template>
  <div class="editor-richtext border rounded" v-if="editor">
    <!-- Toolbar de formatação -->
    <div class="editor-toolbar d-flex flex-wrap align-items-center gap-1 px-2 py-1 border-bottom bg-body-tertiary">
      <button type="button" class="btn btn-sm px-2" :class="editor.isActive('bold') ? 'btn-primary' : 'btn-outline-secondary'" @mousedown.prevent="editor.chain().focus().toggleBold().run()" title="Negrito (Ctrl+B)">
        <i class="bi bi-type-bold"></i>
      </button>
      <button type="button" class="btn btn-sm px-2" :class="editor.isActive('italic') ? 'btn-primary' : 'btn-outline-secondary'" @mousedown.prevent="editor.chain().focus().toggleItalic().run()" title="Itálico (Ctrl+I)">
        <i class="bi bi-type-italic"></i>
      </button>
      <button type="button" class="btn btn-sm px-2" :class="editor.isActive('underline') ? 'btn-primary' : 'btn-outline-secondary'" @mousedown.prevent="editor.chain().focus().toggleUnderline().run()" title="Sublinhado (Ctrl+U)">
        <i class="bi bi-type-underline"></i>
      </button>
      <button type="button" class="btn btn-sm px-2" :class="editor.isActive('strike') ? 'btn-primary' : 'btn-outline-secondary'" @mousedown.prevent="editor.chain().focus().toggleStrike().run()" title="Tachado">
        <i class="bi bi-type-strikethrough"></i>
      </button>
      <span class="toolbar-divider mx-1"></span>
      <button type="button" class="btn btn-sm px-2" :class="editor.isActive('heading', { level: 1 }) ? 'btn-primary' : 'btn-outline-secondary'" @mousedown.prevent="editor.chain().focus().toggleHeading({ level: 1 }).run()" title="Título 1">
        H1
      </button>
      <button type="button" class="btn btn-sm px-2" :class="editor.isActive('heading', { level: 2 }) ? 'btn-primary' : 'btn-outline-secondary'" @mousedown.prevent="editor.chain().focus().toggleHeading({ level: 2 }).run()" title="Título 2">
        H2
      </button>
      <button type="button" class="btn btn-sm px-2" :class="editor.isActive('heading', { level: 3 }) ? 'btn-primary' : 'btn-outline-secondary'" @mousedown.prevent="editor.chain().focus().toggleHeading({ level: 3 }).run()" title="Título 3">
        H3
      </button>
      <span class="toolbar-divider mx-1"></span>
      <button type="button" class="btn btn-sm px-2" :class="editor.isActive('bulletList') ? 'btn-primary' : 'btn-outline-secondary'" @mousedown.prevent="editor.chain().focus().toggleBulletList().run()" title="Lista com marcadores">
        <i class="bi bi-list-ul"></i>
      </button>
      <button type="button" class="btn btn-sm px-2" :class="editor.isActive('orderedList') ? 'btn-primary' : 'btn-outline-secondary'" @mousedown.prevent="editor.chain().focus().toggleOrderedList().run()" title="Lista numerada">
        <i class="bi bi-list-ol"></i>
      </button>
      <span class="toolbar-divider mx-1"></span>
      <button type="button" class="btn btn-sm px-2" :class="editor.isActive('blockquote') ? 'btn-primary' : 'btn-outline-secondary'" @mousedown.prevent="editor.chain().focus().toggleBlockquote().run()" title="Citação">
        <i class="bi bi-quote"></i>
      </button>
      <button type="button" class="btn btn-sm px-2" :class="editor.isActive('codeBlock') ? 'btn-primary' : 'btn-outline-secondary'" @mousedown.prevent="editor.chain().focus().toggleCodeBlock().run()" title="Bloco de código">
        <i class="bi bi-code-slash"></i>
      </button>
      <span class="toolbar-divider mx-1"></span>
      <button type="button" class="btn btn-sm btn-outline-secondary px-2" @mousedown.prevent="inserirLink" title="Inserir link">
        <i class="bi bi-link-45deg"></i>
      </button>
      <button type="button" class="btn btn-sm btn-outline-secondary px-2" @mousedown.prevent="editor.chain().focus().unsetAllMarks().clearNodes().run()" title="Remover formatação">
        <i class="bi bi-eraser"></i>
      </button>
    </div>

    <!-- Editor Tiptap -->
    <editor-content :editor="editor" class="editor-area p-2" />
  </div>
</template>

<script setup>
import { ref, watch, onBeforeUnmount } from 'vue'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: 'Escreva aqui...' }
})

const emit = defineEmits(['update:modelValue'])

// Flag para evitar loop entre editor e watch do modelValue
const isUpdatingFromEditor = ref(false)

const editor = useEditor({
  content: props.modelValue || '',
  extensions: [
    StarterKit.configure({
      // Configura Heading com níveis 1-3
      heading: { levels: [1, 2, 3] }
    }),
    Underline,
    Link.configure({
      openOnClick: false,
      HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' }
    }),
    Placeholder.configure({
      placeholder: props.placeholder
    })
  ],
  onUpdate: ({ editor }) => {
    isUpdatingFromEditor.value = true
    emit('update:modelValue', editor.getHTML())
  }
})

const inserirLink = () => {
  if (!editor.value) return
  const previousUrl = editor.value.getAttributes('link').href
  const url = window.prompt('URL do link:', previousUrl || 'https://')
  if (url === null) return
  if (url === '') {
    editor.value.chain().focus().extendMarkRange('link').unsetLink().run()
    return
  }
  editor.value.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
}

// Sincroniza modelValue externo → editor
watch(() => props.modelValue, (novo) => {
  if (!isUpdatingFromEditor.value && editor.value && novo !== editor.value.getHTML()) {
    editor.value.commands.setContent(novo || '', false)
  }
  isUpdatingFromEditor.value = false
})

onBeforeUnmount(() => {
  editor.value?.destroy()
})
</script>

<style scoped>
.editor-richtext {
  background-color: var(--bs-body-bg);
}
.editor-toolbar {
  border-radius: calc(0.375rem - 1px) calc(0.375rem - 1px) 0 0;
}
.toolbar-divider {
  width: 1px;
  height: 22px;
  background-color: var(--bs-border-color);
}
.editor-area {
  min-height: 180px;
  max-height: 400px;
  overflow-y: auto;
  cursor: text;
}
.editor-area :deep(.ProseMirror) {
  outline: none;
  min-height: 180px;
  font-size: 0.9rem;
  line-height: 1.6;
}
.editor-area :deep(.ProseMirror p) {
  margin: 0.3em 0;
}
.editor-area :deep(.ProseMirror h1) {
  font-size: 1.4rem;
  font-weight: 600;
  margin: 0.5em 0 0.3em;
}
.editor-area :deep(.ProseMirror h2) {
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0.4em 0 0.2em;
}
.editor-area :deep(.ProseMirror h3) {
  font-size: 1.05rem;
  font-weight: 600;
  margin: 0.3em 0 0.2em;
}
.editor-area :deep(.ProseMirror ul),
.editor-area :deep(.ProseMirror ol) {
  padding-left: 1.5em;
  margin: 0.3em 0;
}
.editor-area :deep(.ProseMirror li) {
  margin: 0.1em 0;
}
.editor-area :deep(.ProseMirror blockquote) {
  border-left: 3px solid var(--bs-secondary);
  padding-left: 0.75rem;
  color: var(--bs-secondary-color);
  margin: 0.3em 0;
}
.editor-area :deep(.ProseMirror pre) {
  background-color: var(--bs-body-tertiary);
  padding: 0.5rem;
  border-radius: 0.25rem;
  font-family: var(--bs-font-monospace);
  font-size: 0.85rem;
  margin: 0.3em 0;
  white-space: pre-wrap;
}
.editor-area :deep(.ProseMirror code) {
  background-color: var(--bs-body-tertiary);
  padding: 0.1em 0.3em;
  border-radius: 0.2rem;
  font-family: var(--bs-font-monospace);
  font-size: 0.85em;
}
.editor-area :deep(.ProseMirror a) {
  color: var(--bs-primary);
  text-decoration: underline;
  cursor: pointer;
}
.editor-area :deep(.ProseMirror p.is-editor-empty:first-child::before) {
  content: attr(data-placeholder);
  color: var(--bs-secondary-color);
  pointer-events: none;
  float: left;
  height: 0;
}
</style>
